import { useMemo } from 'react';

import { capitalize } from 'lodash-es';

import { IBackendCartEntries, ILegacyCartEntry } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useGetMenuObjectQuery } from 'state/menu/menu-options/hooks/use-get-menu-object-query';
import { IMenuObject } from 'state/menu/types';
import { useUIContext } from 'state/ui';
import { isDummyCartEntry } from 'utils/cart';
import { CartEntryType, ComboSlotUiPattern } from 'utils/cart/types';
import { getMenuObjectNameMap, maybeMapCartEntryToEntryTypeAndIdForDisplay } from 'utils/menu';
import { IMenuObjectDisplayData } from 'utils/menu/get-menu-object-name-map';

interface UiPatternOption {
  _id?: string;
  uiPattern?: ComboSlotUiPattern | null;
}
interface UiPatternOptionMap {
  [id: string]: UiPatternOption;
}

interface ItemDescription {
  name: string;
  quantity: number;
  price: number;
}

type ItemDescriptionMap = { [key: string]: ItemDescription };

const getDescriptionMap = ({
  cartEntries = [],
  quantity,
  itemDescriptionMap,
  shouldHideCartEntry,
  shouldIncludeModifiers,
  computePrice,
  freshDisplayDataMap,
}: {
  cartEntries?: ILegacyCartEntry['children'] | IBackendCartEntries['children'];
  quantity: number;
  itemDescriptionMap: ItemDescriptionMap;
  shouldHideCartEntry: (c: ILegacyCartEntry | IBackendCartEntries) => boolean;
  shouldIncludeModifiers: (c: ILegacyCartEntry | IBackendCartEntries) => boolean;
  computePrice: (c: ILegacyCartEntry | IBackendCartEntries) => number;
  freshDisplayDataMap: Map<string, IMenuObjectDisplayData>;
}): ItemDescriptionMap => {
  for (const cartEntry of cartEntries) {
    if (shouldHideCartEntry(cartEntry)) {
      continue;
    }

    if (shouldIncludeModifiers(cartEntry)) {
      const freshName = freshDisplayDataMap.get(cartEntry.sanityId)?.name;
      const itemName = (freshName ?? cartEntry.name ?? '').split(' ').map(capitalize).join(' ');
      const price = computePrice(cartEntry);
      const quantityToAdd = quantity * cartEntry.quantity;

      if (itemDescriptionMap[itemName]) {
        // Add up if existing
        itemDescriptionMap[itemName] = {
          ...itemDescriptionMap[itemName],
          quantity: itemDescriptionMap[itemName].quantity + quantityToAdd,
          price: itemDescriptionMap[itemName].price + price,
        };
      } else {
        // Create new entry if empty
        itemDescriptionMap[itemName] = {
          name: itemName,
          quantity: quantityToAdd,
          price,
        };
      }
    }

    // Recursively add children
    itemDescriptionMap = {
      ...itemDescriptionMap,
      ...getDescriptionMap({
        cartEntries: cartEntry.children,
        quantity,
        itemDescriptionMap,
        shouldHideCartEntry,
        shouldIncludeModifiers,
        computePrice,
        freshDisplayDataMap,
      }),
    };
  }
  return itemDescriptionMap;
};

export interface IComposedDescription {
  /** Formatted modifier/combo-slot description lines. */
  descriptions: string[];
  /**
   * The item's own fresh, currently-localized name, when the legacy menu-object lookup
   * resolved one — `undefined` for One Menu (not authoritative there) or on a lookup miss.
   * Exposed so callers that also need the top-level name (e.g. OrderItem, CartEntry) can reuse
   * this hook's single `useGetMenuObjectQuery` call instead of issuing a second, identical one.
   */
  freshName?: string;
}

/**
 * This hook takes a cart entry item and returns description
 * composed of comboSlot & itemOption selections.
 *
 * Note: includeModifiers also controls if we show comboSlot items.
 */
export function useComposeDescription(
  item: ILegacyCartEntry | IBackendCartEntries,
  options: {
    includeModifiers?: boolean;
    includeQuantity?: boolean;
  } = {}
): IComposedDescription {
  const { includeModifiers = false, includeQuantity = false } = options;

  // `menuObject` also feeds getMenuObjectUiPatternMap/getMenuObjectPricesToIgnore below, which
  // this hook already relied on before the fresh-name fix and which IBackendCartEntries has no
  // equivalent for (it doesn't carry uiPattern) — so the query itself must stay unconditional
  // for every store. Only the fresh-name lookup is legacy-only: for a One Menu store, the same
  // sanityId can still resolve a legacy menu object without being the authoritative source, so
  // freshNameMap (not the query) is what must stay disabled there, to avoid overriding the
  // already-correct baked-in name with a legacy variant one (matching OrderItem's equivalent
  // gate on its own, separate lookup).
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { sanityId, menuObjectType } = maybeMapCartEntryToEntryTypeAndIdForDisplay(item);
  const { data: menuObject } = useGetMenuObjectQuery({
    menuObjectType: menuObjectType as MenuObjectTypes,
    sanityId,
  });
  const { formatCurrencyForLocale } = useUIContext();

  const freshName = !isUnifiedMenuEnabled ? menuObject?.name?.locale ?? undefined : undefined;

  const descriptions = useMemo(() => {
    // Function to compute Pricing
    const quantity = item.quantity;
    const itemPricesToIgnore = getMenuObjectPricesToIgnore(menuObject);
    const computePrice = (cartEntry: ILegacyCartEntry | IBackendCartEntries): number => {
      const shouldIgnorePrice = itemPricesToIgnore.has(cartEntry.sanityId);
      return shouldIgnorePrice ? 0 : (cartEntry.price || 0) * quantity;
    };

    // Function to determine Visibility
    const uiPatternMap = getMenuObjectUiPatternMap(menuObject);
    const shouldHideCartEntry = (cartEntry: ILegacyCartEntry | IBackendCartEntries): boolean => {
      const uiPattern =
        ('uiPattern' in cartEntry ? cartEntry.uiPattern : null) || uiPatternMap[cartEntry.sanityId];

      // Dummy items (usually combo slot markers or cartEntry markers) should be hidden
      if (cartEntry.isDummyItem || isDummyCartEntry(cartEntry)) {
        return true;
      }

      return (
        (cartEntry.type === CartEntryType.comboSlot && uiPattern === ComboSlotUiPattern.HIDDEN) ||
        uiPattern === ComboSlotUiPattern.COLLAPSED
      );
    };

    // Function to determine Modifiers criteria
    const shouldIncludeModifiers = (cartEntry: ILegacyCartEntry | IBackendCartEntries): boolean => {
      return (
        cartEntry.type === CartEntryType.item ||
        (includeModifiers && cartEntry.type === CartEntryType.itemOptionModifier)
      );
    };

    // Fresh, currently-localized display data for the nested combo slot/item option/modifier
    // tree. Legacy-only: for a One Menu store, menuObject may still resolve from the legacy
    // Sanity API without being authoritative, so this map must stay empty there — the baked-in
    // name is already correct for One Menu.
    const freshDisplayDataMap = isUnifiedMenuEnabled
      ? new Map<string, IMenuObjectDisplayData>()
      : getMenuObjectNameMap(menuObject);

    // Init recursion
    const itemDescriptionMap = getDescriptionMap({
      cartEntries: item.children || [],
      quantity,
      itemDescriptionMap: {},
      shouldHideCartEntry,
      shouldIncludeModifiers,
      computePrice,
      freshDisplayDataMap,
    });

    // Format results
    return Object.values(itemDescriptionMap).map(itemDescription => {
      const quantityLabel = includeQuantity ? itemDescription.quantity : '';
      const nameLabel = itemDescription.name;
      const priceLabel =
        itemDescription.price !== 0 ? formatCurrencyForLocale(itemDescription.price) : '';
      const priceSign = priceLabel && itemDescription.price > 0 ? '+' : '';

      return `${quantityLabel} ${nameLabel} ${priceSign}${priceLabel}`;
    });
  }, [
    formatCurrencyForLocale,
    includeModifiers,
    includeQuantity,
    isUnifiedMenuEnabled,
    item.children,
    item.quantity,
    menuObject,
  ]);

  return { descriptions, freshName };
}

const getMenuObjectPricesToIgnore = (menuObject: IMenuObject | null | undefined) => {
  const pricesToIgnore = new Set<string>();
  if (!menuObject) {
    return pricesToIgnore;
  }

  const mergedOptions = [
    ...(menuObject?.options || []),
    ...(('mainItem' in menuObject && menuObject?.mainItem?.options) || []),
  ];
  mergedOptions.forEach(option => {
    if (option && 'allowMultipleSelections' in option && Boolean(option.allowMultipleSelections)) {
      (option.options ?? []).forEach(opt => {
        if (opt?._key) {
          pricesToIgnore.add(opt._key);
        }
      });
    }
  });
  return pricesToIgnore;
};

const getMenuObjectUiPatternMap = (menuObject: IMenuObject | null | undefined) => {
  const uiPatternMap = {} as UiPatternOptionMap;

  for (const option of menuObject?.options ?? []) {
    if (option && 'uiPattern' in option) {
      uiPatternMap[option._id] = {
        _id: option._id,
        uiPattern: option.uiPattern as UiPatternOption['uiPattern'],
      };
    }
  }

  return uiPatternMap;
};
